import {
  Document,
  Font,
  Page,
  StyleSheet,
  Text,
  View,
} from "@react-pdf/renderer";
import NunitoBold from "assets/fonts/Nunito-Bold.ttf";
import NunitoMedium from "assets/fonts/Nunito-Medium.ttf";
import NunitoRegular from "assets/fonts/Nunito-Regular.ttf";
import NunitoSemiBold from "assets/fonts/Nunito-SemiBold.ttf";
import { parseFloatData } from "utils/UtilityFuncs";

// --- Styles ---
const styles = StyleSheet.create({
  page: {
    padding: 20,
    // fontFamily: "Helvetica",
    fontSize: 8,
    fontFamily: "Nunito",
  },

  // Header Styles
  headerSection: {
    // marginBottom: 20,
    textAlign: "center",
    fontWeight: "bold",
  },
  companyName: {
    fontSize: 25,
    fontWeight: "bold",
    // marginBottom: 5,
  },
  address: {},

  invoiceTitle: {
    fontSize: 16,
    fontWeight: "bold",
    textAlign: "center",
    textDecoration: "underline",
    // borderBottom: "1px solid #000",
    paddingBottom: 5,
    marginVertical: 10,
  },

  // utility Styles
  borderTop: {
    borderTop: "1px solid #000",
  },
  borderBottom: {
    borderBottom: "1px solid #000",
  },
  borderLeft: {
    borderLeft: "1px solid #000",
  },
  borderRight: {
    borderRight: "1px solid #000",
  },

  // Information Grid Styles
  infoGrid: {
    marginBottom: 15,
  },
  gridRow: {
    flexDirection: "row",
    marginBottom: 2,
    alignItems: "flex-start",
  },

  invoiceInfoCol: {
    width: "20%",
    padding: 5,
    borderRight: "1px solid #000",
    borderLeft: "1px solid #000",
    borderBottom: "1px solid #000",
  },
  invoiceInfoValueCol: {
    width: "50%",
    padding: 5,
    borderRight: "1px solid #000",
    borderBottom: "1px solid #000",
  },
  consigneeCol: {
    width: "30%",
    padding: 5,
    borderRight: "1px solid #000",
  },

  // Box Approach
  invoiceInfoBox: {
    width: "30%",
    borderLeft: "1px solid #000",
    borderTop: "1px solid #000",
    borderBottom: "1px solid #000",
    paddingVertical: 0,
  },
  invoiceInfoValueBox: {
    width: "40%",
    borderTop: "1px solid #000",
    borderBottom: "1px solid #000",
    borderLeft: "1px solid #000",
    paddingVertical: 0,
  },

  consigneeBox: {
    width: "30%",
    borderTop: "1px solid #000",
    borderRight: "1px solid #000",
    borderLeft: "1px solid #000",
    borderBottom: "1px solid #000",
    padding: 5,
  },

  cellBox: {
    justifyContent: "center",
    alignItems: "flex-start",
    paddingVertical: 3,
  },

  // Consignee and Invoice split row
  splitRow: {
    flexDirection: "row",
  },

  labelCol: {
    width: "35%",
    minHeight: 12,
    paddingRight: 5,
  },
  valueCol: {
    width: "65%",
    borderBottom: "1px solid #000",
    paddingLeft: 5,
  },

  // Table Styles
  table: {
    display: "flex",
    width: "auto",
    borderStyle: "solid",
    borderWidth: 1,
    borderRightWidth: 0,
    borderBottomWidth: 0,
    marginBottom: 10,
  },
  tableRow: {
    margin: "auto",
    flexDirection: "row",
  },
  tableHeader: {
    backgroundColor: "#f2f2f2",
    textAlign: "center",
    fontWeight: "bold",
  },
  tableCell: {
    borderStyle: "solid",
    borderWidth: 1,
    borderLeftWidth: 0,
    borderTopWidth: 0,
    padding: 5,
    textAlign: "center",
    // Default width styles - overridden below
  },
  cell_HS_CODE: { width: "15%" },
  cell_DESCRIPTION: { width: "55%" },
  cell_QTY_DESC: { width: "10%" },
  cell_RATE: { width: "8%" },
  cell_AMOUNT: { width: "12%" },

  // Specific Data Cell Styles
  dataCell: {
    textAlign: "left",
    paddingTop: 8,
    paddingBottom: 8,
  },
  centerText: {
    textAlign: "center",
  },
  rightText: {
    textAlign: "right",
  },

  // Total Row Styles
  totalRow: {
    flexDirection: "row",
    borderStyle: "solid",
    borderWidth: 1,
    borderTopWidth: 0,
    borderRightWidth: 0,
    borderLeftWidth: 0,
  },
  totalLabelCell: {
    width: "70%",
    padding: 5,
    textAlign: "right",
    fontWeight: "bold",
  },
  totalValueCell: {
    width: "30%",
    padding: 5,
    textAlign: "right",
    fontWeight: "bold",
    borderLeft: "1px solid #000",
    borderBottom: "1px solid #000",
  },
});

// Helper component for the table data row
const TableDataRow = ({ hsCode, description, quantity, rate }) => (
  <View style={styles.tableRow}>
    <View
      style={[
        styles.tableCell,
        styles.cell_HS_CODE,
        {
          alignContent: "center",
          justifyContent: "center",
        },
      ]}
    >
      <Text style={styles.centerText}>{hsCode}</Text>
    </View>
    <View style={[styles.tableCell, styles.cell_DESCRIPTION, styles.dataCell]}>
      <Text>{description}</Text>
    </View>
    <View style={[styles.tableCell, styles.cell_QTY_DESC, styles.dataCell]}>
      <Text style={styles.centerText}>{quantity} PCS</Text>
    </View>
    <View style={[styles.tableCell, styles.cell_RATE, styles.dataCell]}>
      <Text style={styles.centerText}>
        {parseFloatData(rate?.toFixed(4), 4)}
      </Text>
    </View>
    <View style={[styles.tableCell, styles.cell_AMOUNT, styles.dataCell]}>
      <Text style={styles.centerText}>
        {parseFloatData((quantity * rate).toFixed(4), 2)}
      </Text>
    </View>
  </View>
);

const convertSqlTimestampToDate = (sqlTime, short = false) => {
  const timestamp = sqlTime;
  const date = new Date(timestamp);
  const time = date.toLocaleString("en-PK", {
    year: "numeric",
    month: "numeric",
    day: "numeric",
  });
  return time;
};

// The main PDF component
export const EfsInvoicePdf = ({ data, efsNo }) => {
  // Use provided data or fall back to default invoiceData
  const pdfData = data;

  Font.register({
    family: "Nunito",
    fonts: [
      { src: NunitoRegular },
      { src: NunitoSemiBold, fontWeight: "600" },
      { src: NunitoBold, fontWeight: "bold" },
      { src: NunitoMedium, fontWeight: "medium" },
    ],
  });

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        {/* --- 1. Header Section --- */}
        <View style={styles.headerSection}>
          <Text style={styles.companyName}>SUPER NOVA SPORTS</Text>
          <Text style={styles.address}>
            1212-Sports Street Daska Road, Dakwala Stop Sialkot, Pakistan
          </Text>
          <Text style={styles.address}>Ph # 0092-321 714 9920</Text>
          <Text style={styles.address}>
            NTN#3926897-7 &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;STRN#2800392689711
          </Text>

          <Text style={styles.address}>e-mail : info@novasports.pk</Text>
        </View>

        <View
          style={{
            borderBottom: "1px solid gray",
            marginTop: 10,
            marginBottom: 0,
            width: "100%",
          }}
        ></View>

        {/* --- 2. Invoice Title --- */}
        <Text style={styles.invoiceTitle}>INVOICE</Text>

        {/* New Box Implementation */}
        <View style={styles.infoGrid}>
          <View style={[styles.splitRow]}>
            {/* Invoice Info Box */}
            <View style={[styles.invoiceInfoBox]}>
              <Text
                style={[
                  styles.borderBottom,
                  styles.cellBox,
                  { paddingLeft: 8 },
                ]}
              >
                INVOICE NO:{" "}
              </Text>
              <Text
                style={[
                  styles.borderBottom,
                  styles.cellBox,
                  { paddingLeft: 8 },
                ]}
              >
                INVOICE OF:{" "}
              </Text>
              <Text
                style={[
                  styles.borderBottom,
                  styles.cellBox,
                  { paddingLeft: 8 },
                ]}
              >
                NO. & KIND OF PACKAGES:
              </Text>
              <Text
                style={[
                  styles.borderBottom,
                  styles.cellBox,
                  { paddingLeft: 8 },
                ]}
              >
                SHIPMENT:{" "}
              </Text>
              <Text
                style={[
                  styles.borderBottom,
                  styles.cellBox,
                  { paddingLeft: 8 },
                ]}
              >
                DESTINATION:{" "}
              </Text>
              <Text
                style={[
                  styles.borderBottom,
                  styles.cellBox,
                  { paddingLeft: 8 },
                ]}
              >
                TERMS OF PAYMENT:{" "}
              </Text>
              <Text
                style={[
                  styles.cellBox,
                  {
                    paddingLeft: 8,
                  },
                ]}
              >
                FINANCIAL INSTRUMENT NO. & DATE:{" "}
              </Text>
            </View>
            {/* Invoice Info Value */}
            <View style={[styles.invoiceInfoValueBox]}>
              <View
                style={{
                  flexDirection: "row",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Text
                  style={[
                    styles.borderBottom,
                    styles.cellBox,
                    { paddingLeft: 8, flexGrow: 1 },
                  ]}
                >
                  {pdfData?.invoiceNo || ""}
                </Text>
                <Text
                  style={[
                    styles.borderBottom,
                    styles.cellBox,
                    { paddingRight: 8 },
                  ]}
                >
                  {convertSqlTimestampToDate(pdfData?.invoiceDate) || ""}
                </Text>
              </View>
              <Text
                style={[
                  styles.borderBottom,
                  styles.cellBox,
                  { paddingLeft: 8 },
                ]}
              >
                {pdfData?.invoiceOf || ""}
              </Text>
              <Text
                style={[
                  styles.borderBottom,
                  styles.cellBox,
                  { paddingLeft: 8 },
                ]}
              >
                {pdfData?.noOfPackages || ""} CARTONS
              </Text>
              <Text
                style={[
                  styles.borderBottom,
                  styles.cellBox,
                  { paddingLeft: 8 },
                ]}
              >
                {pdfData?.shipmentType || ""}
              </Text>
              <Text
                style={[
                  styles.borderBottom,
                  styles.cellBox,
                  { paddingLeft: 8 },
                ]}
              >
                {pdfData?.destination || ""}
              </Text>
              <Text
                style={[
                  styles.borderBottom,
                  styles.cellBox,
                  { paddingLeft: 8 },
                ]}
              >
                {pdfData?.termsOfPayment || ""}
              </Text>

              <View
                style={{
                  flexDirection: "row",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Text style={[styles.cellBox, { paddingLeft: 8, flexGrow: 1 }]}>
                  {pdfData?.financialInstrument || ""}
                </Text>
                <Text style={[styles.cellBox, { paddingRight: 8 }]}>
                  {convertSqlTimestampToDate(
                    pdfData?.financialInstrumentDate
                  ) || ""}
                </Text>
              </View>
            </View>
            {/* Consignee Box */}
            <View style={[styles.consigneeBox]}>
              <Text
                style={{
                  fontWeight: "bold",
                  textDecoration: "underline",
                  fontSize: 10,
                  marginBottom: 5,
                  paddingBottom: 0,
                }}
              >
                CONSIGNEE:
              </Text>
              <Text style={{ fontWeight: "bold" }}>
                BILL TO. {pdfData?.billTo || ""}
                {"\n"}
                SHIP TO. {pdfData?.shipTo || ""}
                {"\n"}
                {pdfData?.shipAddress
                  ? `${pdfData?.shipAddress}\nTEL No.${pdfData?.phone}`
                  : ""}
              </Text>
              <View
                style={{
                  marginTop: 5,
                  textAlign: "center",
                }}
              >
                <Text
                  style={[
                    {
                      fontWeight: "bold",
                      paddingBottom: 3,
                      textDecoration: "underline",
                    },
                  ]}
                >
                  R&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;1-
                  {pdfData?.noOfPackages || ""}
                  &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;I
                </Text>
                <Text>{pdfData?.destination || ""}</Text>
              </View>
            </View>
          </View>
        </View>
        {/* --- 4. Table Section --- */}
        <View style={styles.table}>
          {/* Table Header Row */}
          <View style={[styles.tableRow, styles.tableHeader]}>
            <View
              style={[
                styles.tableCell,
                styles.cell_HS_CODE,
                {
                  alignContent: "center",
                  justifyContent: "center",
                },
              ]}
            >
              <Text>HS CODE</Text>
            </View>
            <View
              style={[
                styles.tableCell,
                styles.cell_DESCRIPTION,
                {
                  alignContent: "center",
                  justifyContent: "center",
                },
              ]}
            >
              <Text>DESCRIPTION</Text>
            </View>
            <View
              style={[
                styles.tableCell,
                styles.cell_QTY_DESC,
                {
                  alignContent: "center",
                  justifyContent: "center",
                },
              ]}
            >
              <Text>QUANTITY</Text>
            </View>
            <View
              style={[
                styles.tableCell,
                styles.cell_RATE,
                {
                  alignContent: "center",
                  justifyContent: "center",
                },
              ]}
            >
              <Text>RATE</Text>
              <Text>US$:</Text>
            </View>
            <View
              style={[
                styles.tableCell,
                styles.cell_AMOUNT,
                {
                  alignContent: "center",
                  justifyContent: "center",
                },
              ]}
            >
              <Text>T.AMOUNT</Text>
              <Text>US$:</Text>
            </View>
          </View>

          {/* Empty Row with Efs License No# */}
          <View style={[styles.tableRow, { borderBottom: 0 }]}>
            <View
              style={[
                styles.tableCell,
                styles.cell_HS_CODE,
                {
                  alignContent: "center",
                  justifyContent: "center",
                  borderBottom: 0,
                },
              ]}
            >
              <Text style={styles.centerText}></Text>
            </View>
            <View
              style={[
                styles.tableCell,
                styles.cell_DESCRIPTION,
                styles.dataCell,
                { borderBottom: 0 },
              ]}
            >
              <Text
                style={{
                  fontSize: 10,
                  fontWeight: "bold",
                  textAlign: "center",
                }}
              >
                EFS License # {efsNo || ""}
              </Text>
            </View>
            <View
              style={[
                styles.tableCell,
                styles.cell_QTY_DESC,
                styles.dataCell,
                { borderBottom: 0 },
              ]}
            >
              <Text style={styles.centerText}></Text>
            </View>
            <View
              style={[
                styles.tableCell,
                styles.cell_RATE,
                styles.dataCell,
                { borderBottom: 0 },
              ]}
            >
              <Text style={styles.centerText}></Text>
            </View>
            <View
              style={[
                styles.tableCell,
                styles.cell_AMOUNT,
                styles.dataCell,
                { borderBottom: 0 },
              ]}
            >
              <Text style={styles.centerText}></Text>
            </View>
          </View>

          {/* Data Rows */}
          {(
            pdfData?.items ||
            [
              // {
              //   hsCode: "4202.3900",
              //   description:
              //     "GYM BAG M/O 900D PU COATED FABRIC (IMP) AW ZIPPER (IMP) UNDER EFS LICENSE, ASSORTED COLOR/SIZES",
              //   quantity: 54.0,
              //   rate: 6.0,
              // },
            ]
          ).map((item, index) => (
            <TableDataRow key={index} {...item} />
          ))}

          {/* Total FOB value row */}
          <View
            style={[
              styles.tableRow,
              styles.borderBottom,
              styles.borderRight,
              (!pdfData?.items || pdfData?.items?.length === 0) &&
                styles.borderTop,
              {
                width: "100%",
                flexDirection: "row",
                alignItems: "center",
                justifyContent: "space-between",
              },
            ]}
          >
            <View style={{ flexDirection: "row", width: "70%" }}>
              <Text> TOTAL FOB VALUE US$:</Text>
            </View>

            <View
              style={[
                styles.cell_QTY_DESC,
                styles.borderRight,
                styles.borderLeft,
                { padding: 5 },
              ]}
            >
              <Text style={styles.centerText}>
                {pdfData?.items
                  ? pdfData?.items.reduce((acc, item) => acc + item.quantity, 0)
                  : 0}{" "}
              </Text>
            </View>
            <View
              style={[
                styles.cell_RATE,
                styles.borderRight,
                styles.cell_RATE,
                { padding: 5 },
              ]}
            >
              <Text style={styles.centerText}>US$</Text>
            </View>
            <View
              style={[styles.cell_AMOUNT, styles.cell_AMOUNT, { padding: 5 }]}
            >
              <Text style={styles.centerText}>
                {parseFloatData(
                  pdfData?.items
                    ? parseFloat(
                        pdfData?.items.reduce(
                          (acc, item) => acc + item.quantity * item.rate,
                          0
                        )
                      )?.toFixed(2)
                    : 0.0,
                  2
                )}
              </Text>
            </View>
          </View>
        </View>
      </Page>
    </Document>
  );
};
